// Todo operations for SyncedStore
import { v4 as uuidv4 } from 'uuid';
import { getDoc, getClientId } from './connection';
import type { Todo, WorkerOut, WorkerAssignment, WorkerAssignmentLog, WorkerTimeLog } from './types';

// This will be set by the main store index
 let store: any = null;


export function setStore(storeInstance: any) {
  store = storeInstance;
}

export function addTodo(title: string) {
  const doc = getDoc();
  if (!doc || !store) return;

  // Validate required fields
  if (!title.trim()) {
    throw new Error('Todo title is required');
  }

  const newTodo: Todo = {
    id: uuidv4(),
    title: title.trim(),
    completed: false,
    timestamp: Date.now(),
    clientId: getClientId()
  };

  doc.transact(() => {
    store.todos.push(newTodo);
  });
}

export function editTodo(id: string, newTitle: string) {
  const doc = getDoc();
  if (!doc || !store) return;
  
  // Validate title
  if (!newTitle.trim()) {
    throw new Error('Todo title cannot be empty');
  }
  
  doc.transact(() => {
    const todo = store.todos.find((t: Todo) => t.id === id);
    if (todo) {
      todo.title = newTitle.trim();
      todo.timestamp = Date.now();
      todo.clientId = getClientId();
    }
  });
}

export function toggleTodo(id: string) {
  const doc = getDoc();
  if (!doc || !store) return;
  
  doc.transact(() => {
    const todo = store.todos.find((t: Todo) => t.id === id);
    if (todo) {
      todo.completed = !todo.completed;
      todo.timestamp = Date.now();
      todo.clientId = getClientId();
    }
  });
}

export function deleteTodo(id: string) {
  const doc = getDoc();
  if (!doc || !store) return;
  
  doc.transact(() => {
    const index = store.todos.findIndex((t: Todo) => t.id === id);
    if (index !== -1) {
      store.todos.splice(index, 1);
    }
  });
}

export function addWorkerOut(teamId: string, workerId: string) {
  const doc = getDoc();
  if (!doc || !store) return;

  // Validate required fields
  if (!teamId || !workerId) {
    throw new Error('Team id and worker id are required');
  }

  const newWorkerOut: WorkerOut = {
    id: uuidv4(),
    workerId,
    teamId,
    timestamp: Date.now(),
    clientId: getClientId()?.toString()!
  };

  doc.transact(() => {
    store.workerOuts.push(newWorkerOut);
  });
}

export function revertWorkerOut(workerOutId: string) {
  const doc = getDoc();
  if (!doc || !store) return;

  // Validate required fields
  if (!workerOutId) {
    throw new Error('Worker out id is required');
  }

  doc.transact(() => {
    const workerOut = store.workerOuts.find((w: WorkerOut) => w.id === workerOutId);
    if (workerOut) {
      workerOut.softDeleted = true;
      workerOut.timestamp = Date.now();
      workerOut.clientId = getClientId()?.toString()!;
    }
  });
}

export function addWorkerAssignment(teamId: string, workerId: string, taskTypeId: string, zoneId: string, locationId?: string) {
  const doc = getDoc();
  if (!doc || !store) return;

  // Validate required fields
  if (!teamId || !workerId || !taskTypeId || !zoneId) {
    throw new Error('Team id, worker id, task type id, and zone id are required');
  }

  const newAssignment: WorkerAssignment = {
    id: uuidv4(),
    workerId,
    teamId,
    taskTypeId,
    zoneId,
    locationId,
    timestamp: Date.now(),
    clientId: getClientId()?.toString()!
  };

  doc.transact(() => {
    store.workerAssignments.push(newAssignment);
  });
}

export function unassignWorker(assignmentId: string) {
  const doc = getDoc();
  if (!doc || !store) return;

  // Validate required fields
  if (!assignmentId) {
    throw new Error('Assignment id is required');
  }

  doc.transact(() => {
    const assignment = store.workerAssignments.find((a: WorkerAssignment) => a.id === assignmentId);
    if (assignment) {
      assignment.softDeleted = true;
      assignment.timestamp = Date.now();
      assignment.clientId = getClientId()?.toString()!;
    }
  });
}

export function softDeleteWorkerAssignments(workerId: string) {
  const doc = getDoc();
  if (!doc || !store) return;

  // Validate required fields
  if (!workerId) {
    throw new Error('Worker id is required');
  }

  doc.transact(() => {
    const assignments = store.workerAssignments.filter((a: WorkerAssignment) => 
      a.workerId === workerId && !a.softDeleted
    );
    
    assignments.forEach((assignment: WorkerAssignment) => {
      assignment.softDeleted = true;
      assignment.timestamp = Date.now();
      assignment.clientId = getClientId()?.toString()!;
    });
  });
}

export function getWorkerAssignments(workerId: string) {
  if (!store) return [];

  return store.workerAssignments.filter((a: WorkerAssignment) => 
    a.workerId === workerId && !a.softDeleted
  );
}

export function addWorkerAssignmentLog(
  teamId: string,
  workerId: string,
  taskTypeId: string,
  zoneId: string,
  value: number,
  locationId?: string
) {
  const doc = getDoc();
  if (!doc || !store) return;

  // Validate required fields
  if (!teamId || !workerId || !taskTypeId || !zoneId || value === undefined || value === null) {
    throw new Error('Team id, worker id, task type id, zone id, and value are required');
  }

  const newLog: WorkerAssignmentLog = {
    id: uuidv4(),
    workerId,
    teamId,
    taskTypeId,
    zoneId,
    locationId,
    value,
    timestamp: Date.now(),
    clientId: getClientId()?.toString() || null
  };

  doc.transact(() => {
    store.workerAssignmentLogs.push(newLog);
  });
}

export function softDeleteWorkerAssignmentLog(logId: string) {
  const doc = getDoc();
  if (!doc || !store) return;

  // Validate required fields
  if (!logId) {
    throw new Error('Log id is required');
  }

  doc.transact(() => {
    const log = store.workerAssignmentLogs.find((l: WorkerAssignmentLog) => l.id === logId);
    if (log) {
      log.softDeleted = true;
      log.softDeletedTime = Date.now();
      log.clientId = getClientId()?.toString() || null;
    }
  });
}

export function getWorkerAssignmentLogs(workerId?: string, taskTypeId?: string) {
  if (!store) return [];

  return store.workerAssignmentLogs.filter((l: WorkerAssignmentLog) => {
    const matchesWorker = !workerId || l.workerId === workerId;
    const matchesTask = !taskTypeId || l.taskTypeId === taskTypeId;
    return matchesWorker && matchesTask;
  });
}

export function addWorkerTimeLog(
  workerId: string,
  taskTypeId: string,
  value: number
) {
  const doc = getDoc();
  if (!doc || !store) return;

  // Validate required fields
  if (!workerId || !taskTypeId || value === undefined || value === null) {
    throw new Error('Worker id, task type id, and value are required');
  }

  const newLog: WorkerTimeLog = {
    id: uuidv4(),
    workerId,
    taskTypeId,
    value,
    timestamp: Math.floor(Date.now() / 1000),
    clientId: getClientId()?.toString() || null
  };

  doc.transact(() => {
    store.workerTimeLogs.push(newLog);
  });
}

export function softDeleteWorkerTimeLog(logId: string) {
  const doc = getDoc();
  if (!doc || !store) return;

  // Validate required fields
  if (!logId) {
    throw new Error('Log id is required');
  }

  doc.transact(() => {
    const log = store.workerTimeLogs.find((l: WorkerTimeLog) => l.id === logId);
    if (log) {
      log.softDeleted = true;
      log.softDeletedTime = Math.floor(Date.now() / 1000);
      log.clientId = getClientId()?.toString() || null;
    }
  });
}

export function getWorkerTimeLogs(workerId?: string, taskTypeId?: string) {
  if (!store) return [];

  return store.workerTimeLogs.filter((l: WorkerTimeLog) => {
    const matchesWorker = !workerId || l.workerId === workerId;
    const matchesTask = !taskTypeId || l.taskTypeId === taskTypeId;
    return matchesWorker && matchesTask && !l.softDeleted;
  });
}

export function logStoreData() {
  console.log('=== Store Debug ===');
  console.log('Store reference:', JSON.parse(JSON.stringify(store)));
  console.log('Store is null?', store === null);
}

/**
 * Clean up duplicate entries from the Yjs document
 * Removes duplicates based on ID field for each array
 * Returns report of removals
 */
export function cleanupDuplicates(): {
  success: boolean
  report: {
    teams: number
    workers: number
    zones: number
    taskTypes: number
    locations: number
    workerAssignments: number
    workerOuts: number
    workerAssignmentLogs: number
    workerTimeLogs: number
    todos: number
    total: number
  }
  error?: string
} {
  const doc = getDoc()
  if (!doc || !store) {
    return {
      success: false,
      report: {
        teams: 0,
        workers: 0,
        zones: 0,
        taskTypes: 0,
        locations: 0,
        workerAssignments: 0,
        workerOuts: 0,
        workerAssignmentLogs: 0,
        workerTimeLogs: 0,
        todos: 0,
        total: 0,
      },
      error: 'Store not initialized',
    }
  }

  const report = {
    teams: 0,
    workers: 0,
    zones: 0,
    taskTypes: 0,
    locations: 0,
    workerAssignments: 0,
    workerOuts: 0,
    workerAssignmentLogs: 0,
    workerTimeLogs: 0,
    todos: 0,
    total: 0,
  }

  try {
    doc.transact(() => {
      // Clean teams
      const teamIds = new Set<string>()
      for (let i = store.teams.length - 1; i >= 0; i--) {
        const team = store.teams[i]
        if (team && team.id) {
          if (teamIds.has(team.id)) {
            store.teams.splice(i, 1)
            report.teams++
          } else {
            teamIds.add(team.id)
          }
        }
      }

      // Clean workers
      const workerIds = new Set<string>()
      for (let i = store.workers.length - 1; i >= 0; i--) {
        const worker = store.workers[i]
        if (worker && worker.id) {
          if (workerIds.has(worker.id)) {
            store.workers.splice(i, 1)
            report.workers++
          } else {
            workerIds.add(worker.id)
          }
        }
      }

      // Clean zones
      const zoneIds = new Set<string>()
      for (let i = store.zones.length - 1; i >= 0; i--) {
        const zone = store.zones[i]
        if (zone && zone.id) {
          if (zoneIds.has(zone.id)) {
            store.zones.splice(i, 1)
            report.zones++
          } else {
            zoneIds.add(zone.id)
          }
        }
      }

      // Clean taskTypes
      const taskTypeIds = new Set<string>()
      for (let i = store.taskTypes.length - 1; i >= 0; i--) {
        const taskType = store.taskTypes[i]
        if (taskType && taskType.id) {
          if (taskTypeIds.has(taskType.id)) {
            store.taskTypes.splice(i, 1)
            report.taskTypes++
          } else {
            taskTypeIds.add(taskType.id)
          }
        }
      }

      // Clean locations
      const locationIds = new Set<string>()
      for (let i = store.locations.length - 1; i >= 0; i--) {
        const location = store.locations[i]
        if (location && location.id) {
          if (locationIds.has(location.id)) {
            store.locations.splice(i, 1)
            report.locations++
          } else {
            locationIds.add(location.id)
          }
        }
      }

      // Clean workerAssignments
      const assignmentIds = new Set<string>()
      for (let i = store.workerAssignments.length - 1; i >= 0; i--) {
        const assignment = store.workerAssignments[i]
        if (assignment && assignment.id) {
          if (assignmentIds.has(assignment.id)) {
            store.workerAssignments.splice(i, 1)
            report.workerAssignments++
          } else {
            assignmentIds.add(assignment.id)
          }
        }
      }

      // Clean workerOuts
      const workerOutIds = new Set<string>()
      for (let i = store.workerOuts.length - 1; i >= 0; i--) {
        const workerOut = store.workerOuts[i]
        if (workerOut && workerOut.id) {
          if (workerOutIds.has(workerOut.id)) {
            store.workerOuts.splice(i, 1)
            report.workerOuts++
          } else {
            workerOutIds.add(workerOut.id)
          }
        }
      }

      // Clean workerAssignmentLogs
      const logIds = new Set<string>()
      for (let i = store.workerAssignmentLogs.length - 1; i >= 0; i--) {
        const log = store.workerAssignmentLogs[i]
        if (log && log.id) {
          if (logIds.has(log.id)) {
            store.workerAssignmentLogs.splice(i, 1)
            report.workerAssignmentLogs++
          } else {
            logIds.add(log.id)
          }
        }
      }

      // Clean workerTimeLogs
      const timeLogIds = new Set<string>()
      for (let i = store.workerTimeLogs.length - 1; i >= 0; i--) {
        const log = store.workerTimeLogs[i]
        if (log && log.id) {
          if (timeLogIds.has(log.id)) {
            store.workerTimeLogs.splice(i, 1)
            report.workerTimeLogs++
          } else {
            timeLogIds.add(log.id)
          }
        }
      }

      // Clean todos
      const todoIds = new Set<string>()
      for (let i = store.todos.length - 1; i >= 0; i--) {
        const todo = store.todos[i]
        if (todo && todo.id) {
          if (todoIds.has(todo.id)) {
            store.todos.splice(i, 1)
            report.todos++
          } else {
            todoIds.add(todo.id)
          }
        }
      }

      // Calculate total
      report.total =
        report.teams +
        report.workers +
        report.zones +
        report.taskTypes +
        report.locations +
        report.workerAssignments +
        report.workerOuts +
        report.workerAssignmentLogs +
        report.workerTimeLogs +
        report.todos
    })

    return {
      success: true,
      report,
    }
  } catch (error) {
    console.error('Error cleaning duplicates:', error)
    return {
      success: false,
      report,
      error: error instanceof Error ? error.message : 'Unknown error',
    }
  }
}
